/**
 * useMyLibraryCategoryActions
 * ---
 * Extracted from `src/pages/MyLibrary.tsx` for size discipline. Bundles the
 * five category mutation handlers (create, set-default, update, delete, merge)
 * and re-exposes the state setters they need to update the page in-place.
 *
 * Handler-factory pattern: pass setters in so React's reactive scope is
 * preserved without forward-reference errors.
 */
import { useCallback } from "react";
import {
  createLibraryCategory,
  deleteLibraryCategory,
  mergeLibraryCategory,
  setWorkspaceDefaultLibraryCategory,
  updateLibraryCategory,
  type LibraryCategory,
  type LibraryCategoryColorKey,
} from "@/lib/libraryCategories";
import type { LibraryRecord } from "./myLibraryHelpers";

type Args = {
  active: { id: string } | null;
  editingItem: LibraryRecord | null;
  defaultCategoryId: string | null;
  setDefaultCategoryId: (v: string | null) => void;
  setCategories: React.Dispatch<React.SetStateAction<LibraryCategory[]>>;
  setCategoryUsageCounts: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  setSelectedCategoryIds: React.Dispatch<React.SetStateAction<string[]>>;
  setEditorCategoryIds: React.Dispatch<React.SetStateAction<string[]>>;
  applyCategoryToItems: (next: LibraryCategory[]) => void;
  refreshLibrary: () => Promise<void>;
};

export const useMyLibraryCategoryActions = ({
  active,
  editingItem,
  defaultCategoryId,
  setDefaultCategoryId,
  setCategories,
  setCategoryUsageCounts,
  setSelectedCategoryIds,
  setEditorCategoryIds,
  applyCategoryToItems,
  refreshLibrary,
}: Args) => {
  const handleCreateCategory = useCallback(
    async (values: { name: string; color: LibraryCategoryColorKey }) => {
      if (!active) throw new Error("Select a workspace first.");
      const created = await createLibraryCategory(active.id, values);
      setCategories((current) =>
        [...current, created].sort((a, b) => a.name.localeCompare(b.name)),
      );
      return created;
    },
    [active, setCategories],
  );

  const handleSetDefaultCategory = useCallback(
    async (categoryId: string) => {
      if (!active) throw new Error("Select a workspace first.");
      await setWorkspaceDefaultLibraryCategory(active.id, categoryId);
      setDefaultCategoryId(categoryId);
      if (!editingItem) {
        setEditorCategoryIds((current) => (current.length ? current : [categoryId]));
      }
    },
    [active, editingItem, setDefaultCategoryId, setEditorCategoryIds],
  );

  const handleUpdateCategory = useCallback(
    async (categoryId: string, values: { name: string; color: LibraryCategoryColorKey }) => {
      const updated = await updateLibraryCategory(categoryId, values);
      setCategories((current) => {
        const next = current
          .map((category) => (category.id === updated.id ? updated : category))
          .sort((a, b) => a.name.localeCompare(b.name));
        applyCategoryToItems(next);
        return next;
      });
    },
    [applyCategoryToItems, setCategories],
  );

  const handleDeleteCategory = useCallback(
    async (categoryId: string) => {
      await deleteLibraryCategory(categoryId);
      setCategories((current) => {
        const next = current.filter((category) => category.id !== categoryId);
        applyCategoryToItems(next);
        return next;
      });
      setCategoryUsageCounts((current) => {
        if (!(categoryId in current)) return current;
        const next = { ...current };
        delete next[categoryId];
        return next;
      });
      setSelectedCategoryIds((current) => current.filter((id) => id !== categoryId));
      setEditorCategoryIds((current) => current.filter((id) => id !== categoryId));
      if (defaultCategoryId === categoryId) setDefaultCategoryId(null);
    },
    [
      applyCategoryToItems,
      defaultCategoryId,
      setCategories,
      setCategoryUsageCounts,
      setDefaultCategoryId,
      setEditorCategoryIds,
      setSelectedCategoryIds,
    ],
  );

  const handleMergeCategory = useCallback(
    async (sourceId: string, targetId: string) => {
      await mergeLibraryCategory(sourceId, targetId);
      if (defaultCategoryId === sourceId) setDefaultCategoryId(null);
      setSelectedCategoryIds((current) =>
        Array.from(new Set(current.map((id) => (id === sourceId ? targetId : id)))).filter(
          (id) => id !== sourceId,
        ),
      );
      setEditorCategoryIds((current) =>
        Array.from(new Set(current.map((id) => (id === sourceId ? targetId : id)))).filter(
          (id) => id !== sourceId,
        ),
      );
      await refreshLibrary();
    },
    [defaultCategoryId, refreshLibrary, setDefaultCategoryId, setEditorCategoryIds, setSelectedCategoryIds],
  );

  return {
    handleCreateCategory,
    handleSetDefaultCategory,
    handleUpdateCategory,
    handleDeleteCategory,
    handleMergeCategory,
  };
};
